// ErrorBoundary.js
import React from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";

const ErrorPageContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  background: linear-gradient(135deg, #f5f7fa 0%, #e9ecef 100%);
  padding: 20px;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', sans-serif;
`;

const ErrorContent = styled.div`
  text-align: center;
  max-width: 600px;
  animation: fadeIn 0.6s ease-in;

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const ErrorIcon = styled.div`
  width: 200px;
  height: 200px;
  margin: 0 auto 30px;
  position: relative;

  svg {
    width: 100%;
    height: 100%;
    filter: drop-shadow(0 10px 30px rgba(0, 0, 0, 0.1));
  }
`;

const ErrorTitle = styled.h1`
  font-size: 32px;
  font-weight: 700;
  color: #2c3e50;
  margin-bottom: 16px;
  letter-spacing: -0.5px;
`;

const ErrorMessage = styled.p`
  font-size: 16px;
  color: #6c757d;
  line-height: 1.6;
  margin-bottom: 12px;
`;

const ErrorDetails = styled.p`
  font-size: 14px;
  color: #adb5bd;
  margin-bottom: 40px;
  line-height: 1.5;
`;

const HomeButton = styled.button`
  background: #007bff;
  color: white;
  border: none;
  padding: 14px 40px;
  font-size: 16px;
  font-weight: 600;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(0, 123, 255, 0.3);
  display: inline-flex;
  align-items: center;
  gap: 10px;

  &:hover {
    background: #0056b3;
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(0, 123, 255, 0.4);
  }

  &:active {
    transform: translateY(0);
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

const SecondaryActions = styled.div`
  margin-top: 24px;
  display: flex;
  gap: 16px;
  justify-content: center;
  flex-wrap: wrap;
`;

const SecondaryButton = styled.button`
  background: transparent;
  color: #6c757d;
  border: 2px solid #dee2e6;
  padding: 10px 24px;
  font-size: 14px;
  font-weight: 500;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    border-color: #007bff;
    color: #007bff;
    background: rgba(0, 123, 255, 0.05);
  }
`;

class ErrorBoundaryConainer extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false };
    }

    static getDerivedStateFromError(error) {
        return { hasError: true };
    }

    componentDidCatch(error, errorInfo) {
        console.error("Error caught by ErrorBoundary:", error, errorInfo);
    }

    handleGoHome = () => {
        if (this.props.navigate) {
            this.props.navigate('/');
        } else {
            window.location.href = '/';
        }
    };

    handleRefresh = () => {
        window.location.reload();
    };

    handleGoBack = () => {
        window.history.back();
    };
    a
    render() {
        if (this.state.hasError) {
            return this.props.fallback || <ErrorPageContainer>
                <ErrorContent>
                    <ErrorIcon>
                        <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <circle cx="100" cy="100" r="90" fill="#FFF5F5" />
                            <circle cx="100" cy="100" r="70" fill="#FEE2E2" />
                            <path
                                d="M100 50C72.3858 50 50 72.3858 50 100C50 127.614 72.3858 150 100 150C127.614 150 150 127.614 150 100C150 72.3858 127.614 50 100 50Z"
                                fill="#DC2626"
                                fillOpacity="0.1"
                            />
                            <circle cx="78" cy="90" r="6" fill="#DC2626" />
                            <circle cx="122" cy="90" r="6" fill="#DC2626" />
                            <path
                                d="M75 120C75 120 85 110 100 110C115 110 125 120 125 120"
                                stroke="#DC2626"
                                strokeWidth="4"
                                strokeLinecap="round"
                            />
                            <path
                                d="M140 40L160 20M60 40L40 20"
                                stroke="#FCA5A5"
                                strokeWidth="3"
                                strokeLinecap="round"
                            />
                            <circle cx="170" cy="100" r="5" fill="#FCA5A5" />
                            <circle cx="30" cy="100" r="5" fill="#FCA5A5" />
                        </svg>
                    </ErrorIcon>

                    <ErrorTitle>Oops! Something Went Wrong</ErrorTitle>

                    <ErrorMessage>
                        We're sorry, but something unexpected happened.
                    </ErrorMessage>

                    <ErrorDetails>
                        Don't worry, it's not your fault. Our team has been notified and we're working on fixing this issue.
                    </ErrorDetails>

                    <HomeButton onClick={this.handleGoHome}>
                        <svg viewBox="0 0 20 20" fill="currentColor">
                            <path d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z" />
                        </svg>
                        Go to Home
                    </HomeButton>

                    <SecondaryActions>
                        <SecondaryButton onClick={this.handleGoBack}>Go Back</SecondaryButton>
                        <SecondaryButton onClick={this.handleRefresh}>Refresh Page</SecondaryButton>
                    </SecondaryActions>
                </ErrorContent>
            </ErrorPageContainer>;
        }
        return this.props.children;
    }
}


function ErrorBoundary({ children }) {
  const navigate = useNavigate();
  return <ErrorBoundaryConainer navigate={navigate}>{children}</ErrorBoundaryConainer>;
}

export default ErrorBoundary;
